const SIGN_UP_ERROR_MARKER = "SignUpError:"
const COGNITO_WRAPPED_ERROR_MARKER = `failed with error ${SIGN_UP_ERROR_MARKER}`

// Error name thrown by the Microsoft sign-in when the email belongs to an
// unconfirmed account that this attempt did not create
// (components/feature/Authenticaton/microsoftSignUp.ts).
export const UNCONFIRMED_SIGN_UP_EXISTS_ERROR = "UnconfirmedSignUpExistsError"

export type SignUpErrorKey =
  | "SIGNUP/CORPORATE_EMAIL_REQUIRED"
  | "SIGNUP/EMAIL_ALREADY_REGISTERED"
  | "SIGNUP/COMPANY_TRIAL_CAP_REACHED"
  | "SIGNUP/UNCONFIRMED_ACCOUNT_EXISTS"

// Opening phrases of the pre-signup trigger's rejections
// (lambdas/auth/pre-signup-trigger/lambda_function.py). Any other
// SignUpError is the blocked-domain rejection.
const SIGN_UP_ERROR_PHRASES: [string, SignUpErrorKey][] = [
  [
    "This email address is already registered",
    "SIGNUP/EMAIL_ALREADY_REGISTERED",
  ],
  [
    "Your company already has trial accounts",
    "SIGNUP/COMPANY_TRIAL_CAP_REACHED",
  ],
]

function lambdaErrorText(message: string | null | undefined): string | null {
  const normalizedMessage = message?.trim()
  if (!normalizedMessage) return null

  if (normalizedMessage.startsWith(SIGN_UP_ERROR_MARKER)) {
    return normalizedMessage.slice(SIGN_UP_ERROR_MARKER.length).trim()
  }
  const wrapped = normalizedMessage.indexOf(COGNITO_WRAPPED_ERROR_MARKER)
  if (wrapped === -1) return null
  return normalizedMessage
    .slice(wrapped + COGNITO_WRAPPED_ERROR_MARKER.length)
    .trim()
}

/** Translation key for a pre-signup rejection message, or null if the
 *  message is not one (also used for the hosted UI `error_description`). */
export function getSignUpErrorKey(
  message: string | null | undefined,
): SignUpErrorKey | null {
  const text = lambdaErrorText(message)
  if (text === null) return null

  const match = SIGN_UP_ERROR_PHRASES.find(([phrase]) =>
    text.startsWith(phrase),
  )
  return match ? match[1] : "SIGNUP/CORPORATE_EMAIL_REQUIRED"
}

export function getSignUpErrorKeyFromError(
  error: unknown,
): SignUpErrorKey | null {
  if (!(error instanceof Error)) return null
  if (error.name === UNCONFIRMED_SIGN_UP_EXISTS_ERROR) {
    return "SIGNUP/UNCONFIRMED_ACCOUNT_EXISTS"
  }
  if (error.name !== "UserLambdaValidationException") return null
  return getSignUpErrorKey(error.message)
}

export type SignInErrorKey =
  | "LOGIN/WRONG_CREDENTIALS"
  | "LOGIN/WRONG_CREDENTIALS_GOOGLE"
  | "LOGIN/WRONG_CREDENTIALS_MICROSOFT"

// Other NotAuthorizedException messages (disabled user, attempts exceeded)
// keep Cognito's own text.
const WRONG_PASSWORD_MESSAGE = "Incorrect username or password."

/** Translation key for a wrong-credentials sign-in error, or null if the
 *  error is not one. An unknown user gets the same text as a wrong password
 *  (no account enumeration); the SSO method last used on this device picks
 *  the hint. */
export function getSignInErrorKeyFromError(
  error: unknown,
  lastSignInMethod: string | null,
): SignInErrorKey | null {
  if (!(error instanceof Error)) return null
  const isWrongCredentials =
    error.name === "UserNotFoundException" ||
    (error.name === "NotAuthorizedException" &&
      error.message === WRONG_PASSWORD_MESSAGE)
  if (!isWrongCredentials) return null
  if (lastSignInMethod === "google") return "LOGIN/WRONG_CREDENTIALS_GOOGLE"
  if (lastSignInMethod === "microsoft") {
    return "LOGIN/WRONG_CREDENTIALS_MICROSOFT"
  }
  return "LOGIN/WRONG_CREDENTIALS"
}
